import { FC, PropsWithChildren, createContext, useCallback, use, useEffect, useMemo, useState } from 'react';
import { useAtom, WritableAtom } from 'jotai';
import { PersistentColumnProvider as PersistentColumnProviderType } from '../types';

interface PersistentColumnProviderProps extends PropsWithChildren {
  initialColumns: string[];
  persistenceAtom: WritableAtom<string[], [selectedFields: string[]], void>;
  onSave?: (columns: string[]) => Promise<void>;
  onReset?: () => Promise<void>;
}

const PersistentColumnContext = createContext<PersistentColumnProviderType | undefined>(undefined);

export const PersistentColumnProvider: FC<PersistentColumnProviderProps> = ({
  children,
  initialColumns,
  persistenceAtom,
  onSave: customOnSave,
  onReset: customOnReset,
}) => {
  const [savedSelectedColumns, setSavedSelectedColumns] = useAtom(persistenceAtom);
  const [isSaving, setIsSaving] = useState(false);
  const [localSelectedColumns, setLocalSelectedColumns] = useState<string[]>(initialColumns);

  // Update local state when initial columns change
  useEffect(() => {
    setLocalSelectedColumns(initialColumns);
  }, [initialColumns]);

  // Check if there are changes by comparing with saved state
  const hasChanges = useMemo(() => {
    const currentSorted = [...localSelectedColumns].sort();
    const savedSorted = [...savedSelectedColumns].sort();
    return (
      currentSorted.length !== savedSorted.length ||
      currentSorted.some((item, index) => item !== savedSorted[index])
    );
  }, [localSelectedColumns, savedSelectedColumns]);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      if (customOnSave) {
        await customOnSave(localSelectedColumns);
      } else {
        // Default behavior: update the atom
        setSavedSelectedColumns(localSelectedColumns);
      }
    } catch (error) {
      console.error('Failed to save column selection:', error);
    } finally {
      setIsSaving(false);
    }
  }, [localSelectedColumns, setSavedSelectedColumns, customOnSave]);

  const handleReset = useCallback(async () => {
    setIsSaving(true);
    try {
      if (customOnReset) {
        await customOnReset();
      } else {
        // Default behavior: reset to saved state
        setLocalSelectedColumns(savedSelectedColumns);
      }
    } catch (error) {
      console.error('Failed to reset column selection:', error);
    } finally {
      setIsSaving(false);
    }
  }, [savedSelectedColumns, setLocalSelectedColumns, customOnReset]);

  const value = useMemo(
    () => ({
      selectedColumns: localSelectedColumns,
      setSelectedColumns: setLocalSelectedColumns,
      hasChanges,
      onSave: handleSave,
      onReset: handleReset,
      isSaving,
    }),
    [localSelectedColumns, setLocalSelectedColumns, hasChanges, handleSave, handleReset, isSaving]
  );

  return <PersistentColumnContext value={value}>{children}</PersistentColumnContext>;
};

export const usePersistentColumnProvider = (): PersistentColumnProviderType => {
  const context = use(PersistentColumnContext);

  if (typeof context === 'undefined') {
    throw new Error('usePersistentColumnProvider cannot be used outside PersistentColumnProvider');
  }

  return context;
};
